import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, Typography } from '@material-ui/core';
import styles from '../../styles.module.css';
import Button from '../../../components/Button/index.js';
import Notification from '../../../components/Notification/index.js';
import FormGroup from '../../../components/FormGroup/index.js';
import { ReactComponent as EmailIcon } from '../../../svg/email.svg';
import { validateEmail } from '../../helpers.js';

function EmailSignIn({
  description,
  showForgotPassword,
  onEmailSubmit,
  error: backendError,
  title,
}) {
  const [emailError, setEmailError] = useState('');
  const [needHelpModalShow, setNeedHelpModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const submitForm = useCallback(
    async (event) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);
      const emailData = formData.get('email').trim();

      if (!emailData) {
        setEmailError('Please enter a valid email address');
        return;
      }

      const emailIsValid = validateEmail(emailData);

      if (!emailIsValid) {
        setEmailError('Please enter a valid email address');
        return;
      }

      setEmailError('');
      setIsLoading(true);

      try {
        await onEmailSubmit(emailData);
      } finally {
        setIsLoading(false);
      }
    },
    [onEmailSubmit],
  );

  return (
    <div>
      <div className={styles.headerBlock}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      {backendError && (
        <div className={styles.notification}>
          <Notification
            type="error"
            message={
              <>
                {backendError}&nbsp;
                <Link
                  to="#"
                  onClick={() => {
                    setNeedHelpModalShow(true);
                  }}
                >
                  Need help?
                </Link>
              </>
            }
          />
        </div>
      )}

      <Dialog
        fullWidth
        open={needHelpModalShow}
        onClose={() => {
          setNeedHelpModalShow(false);
        }}
      >
        <DialogContent>
          <Typography variant="h5">Possible reasons:</Typography>
          <ul className={styles.modalUl}>
            <li>Email address is not recognized</li>
            <li>Your account is locked or disabled</li>
            <li>Network connection issues</li>
          </ul>
          <>
            Still stuck?&nbsp;
            <Link to="/forgot-password">Reset your password</Link>
            &nbsp;or contact your admin.
          </>
        </DialogContent>
      </Dialog>

      <form onSubmit={submitForm}>
        <div className={styles.formGroup}>
          <FormGroup
            label="Email"
            type="email"
            name="email"
            icon={<EmailIcon />}
            error={emailError}
          />
        </div>

        <div className={styles.button}>
          <Button
            text={isLoading ? 'Checking...' : 'Continue'}
            submit
            disabled={isLoading}
          />
        </div>

        {showForgotPassword && (
          <div className={styles.forgotLink}>
            <Link to="/forgot-password" className={styles.link}>
              Forgot Password?
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}

EmailSignIn.propTypes = {
  description: PropTypes.string,
  showForgotPassword: PropTypes.bool,
  onEmailSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  title: PropTypes.string,
};

EmailSignIn.defaultProps = {
  description: 'Enter your email address to continue',
  showForgotPassword: true,
  title: 'Welcome Back',
};

export default EmailSignIn;
